import styled from "@emotion/styled";
import GoogleIcon from "@icons/icon-google-logo.png";
import LockIcon from "@mui/icons-material/Lock";
import { Form, Formik, FormikConfig, FormikHelpers } from "formik";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ButtonDefault from "@/shared/components/button/button";
import { TogglePasswordIcon } from "@/shared/components/toggle-password-icon";
import { catchServerError } from "@/shared/helpers/catch-server-error";
import useCookieSessionManager from "@rf-hooks/useCookieSessionManager";
import { PATH } from "@/shared/models/const/path";
import { LoginEndpoints } from "@/shared/models/endpoints/user-auth.endpoint";
import ENVIRONMENT_VAR from "@/shared/models/environment/environments.var";
import { ILoginResponseData } from "@/shared/models/interfaces/response/auth/login-response.interface";
import { useLoginMutation } from "@/shared/services/qrtk/auth/user-auth.qrtk";
import FormWrapped from "../components/wrapper-content";
import { IValuesLogin, formValidations, initialValues } from "./util/form.util";

import Grid2 from "@mui/material/Unstable_Grid2";
import useAlert from "@rf-hooks/useAlert";
import MxFormL3InputField from "@rf-l3/form/MxFormL3InputField";
import MxTypography from "@rf-l1/MxTypography";
import { EFontFamilies } from "@/shared/theme/theme";
import { Grid } from "@mui/material";

function LoginPage() {
  const { alert } = useAlert();
  const { setSessionTokenUser } = useCookieSessionManager();
  const [seePassword, setStateSeePassword] = useState<boolean>(false);
  const [LOGIN_M, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();

  const handlePasswordToggle = () => {
    setStateSeePassword(prev => !prev);
  };

  const handleSubmit = (values: IValuesLogin, fH: FormikHelpers<IValuesLogin>) => {
    const body = {
      email: values.email?.trim(),
      password: values.password
    };
    LOGIN_M(body as any)
      .unwrap()
      .then(res => successLogin(res, fH))
      .catch(catchErroLogin)
      .finally(() => {
        fH.setSubmitting(false);
      });
  };

  const successLogin = async (
    res: ILoginResponseData,
    fH: FormikHelpers<IValuesLogin>
  ) => {
    saveNewSession(res);
    // fH.resetForm();
    // goToApp();
  };

  const catchErroLogin = (err: any) => {
    alert({
      title: "Atención",
      message: catchServerError(err),
      type: "warning",
      color: "#fb923c",
      confirmBtnMessage: "Ok"
    });
  };

  const saveNewSession = (res: ILoginResponseData) => {
    setSessionTokenUser({
      token: res.token,
      maxCoreTokenV1: res.maxCoreTokenV1
    });
  };

  const goToApp = () => {
    navigate("/", { replace: true });
  };

  const formikConfig: FormikConfig<IValuesLogin> = {
    initialValues,
    onSubmit: handleSubmit,
    validationSchema: formValidations,
    enableReinitialize: true
  };

  return (
    <FormWrapped>
      <Formik {...formikConfig}>
        {Fprops => (
          <Grid
            component={Form}
            container
            justifyContent="center"
            alignItems={"center"}
            gap={2}
            mt={2.5}
          >
            <Grid container item xs={12} gap={1}>
              <MxTypography
                variant="subtitle2"
                fontFamily={EFontFamilies.DISPLAY}
                fontWeight={600}
                color="primary.main"
              >
                Usuario
              </MxTypography>
              <MxFormL3InputField
                name="email"
                label="Usuario"
                placeholder=""
                id="email-login"
                InputLabelProps={{
                  sx: {
                    display: "none"
                  },
                  shrink: false
                }}
              />
            </Grid>

            <Grid item xs={12} gap={1} container>
              <MxTypography
                variant="subtitle2"
                fontFamily={EFontFamilies.DISPLAY}
                fontWeight={600}
                color="primary.main"
              >
                Contraseña
              </MxTypography>
              <MxFormL3InputField
                name="password"
                id="password-login"
                placeholder=""
                type={seePassword ? "text" : "password"}
                InputLabelProps={{
                  sx: {
                    display: "none"
                  },
                  shrink: false
                }}
                endIcon={
                  <TogglePasswordIcon
                    handleToggle={handlePasswordToggle}
                    showPassword={seePassword}
                  />
                }
              />
            </Grid>

            <Grid2 container justifyContent="center">
              <CustomButton
                variant="contained"
                disabled={isLoading}
                type="submit"
                sx={{ mb: 2 }}
              >
                Iniciar sesión
              </CustomButton>

              <SocialButton
                href={
                  ENVIRONMENT_VAR.API_URL + LoginEndpoints.LoggedUserGoogleRedirect
                }
                variant="contained"
                type="submit"
                sx={{ mb: 2 }}
              >
                <img src={GoogleIcon} alt="google-icon" />
                Iniciar sesión con Google
              </SocialButton>

              <CustomLink to={PATH.PUBLIC.AUTH.FORGOT_PASSWORD}>
                <CustomIcon />
                Olvidé mi contraseña
              </CustomLink>
            </Grid2>
          </Grid>
        )}
      </Formik>
    </FormWrapped>
  );
}

export default LoginPage;

const CustomButton = styled(ButtonDefault)`
  width: 250px;
  font-family: "Inter", sans-serif;

  background-color: var(--color-blue-secondary);
  &:hover {
    background-color: #2a6097;
    box-shadow: inherit;
  }
`;

const SocialButton = styled(ButtonDefault)`
  width: 250px;
  padding: 3;
  display: flex;
  gap: 10px;
  background-color: #fff;
  font-family: Inter, sans-serif;
  border: 1px solid var(--color-gray);
  color: #636363;
  white-space: nowrap;
  box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px,
    rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;
  &:hover {
    background-color: inherit;
    box-shadow: inherit;
    border: 1px solid #bcbcbc;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const CustomLink = styled(Link)`
  font-size: ${15 / 16}rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-blue-main);
  font-weight: 400;
  gap: 3px;
`;

const CustomIcon = styled(LockIcon)`
  color: var(--color-blue-main);
  font-size: ${15 / 16}rem;
`;
